// ===========================================
// Avatar Types (Frontend)
// ===========================================
// Types for user avatar upload feature (Priority 11)
//
// IMPORTANT: This is for USER photo uploads (Admin, Teacher, Parent).
// Students have a SEPARATE avatar system using character sprites.
// ===========================================

// ===========================================
// ENUMS & CONSTANTS
// ===========================================

export type UserRole = 'ADMIN' | 'TEACHER' | 'PARENT' | 'STUDENT';

/**
 * The DEFAULT colour per role (the brand palette, equal to the backend's `DEFAULT_ROLE_COLORS`).
 * A screen colours a role with the SCHOOL's setting through `useRoleColorScheme` (#629), never
 * with this table directly; it is only the fallback while that loads.
 */
export const ROLE_AVATAR_COLORS: Record<UserRole, { background: string; text: string }> = {
  ADMIN: { background: '#4580E4', text: '#ffffff' },    // Blue - Primary
  TEACHER: { background: '#FFCE00', text: '#080808' },  // Yellow - Secondary
  PARENT: { background: '#96DAC9', text: '#080808' },   // Mint - Accent
  STUDENT: { background: '#FFAE9E', text: '#080808' },  // Coral - Accent
};

/**
 * Available thumbnail sizes (in pixels)
 */
export const AVATAR_SIZES = {
  SMALL: 32,   // List items, small UI elements
  MEDIUM: 64,  // Default size for most uses
  LARGE: 128,  // Profile headers
  ORIGINAL: 512, // Full-size display
} as const;

export type AvatarSize = keyof typeof AVATAR_SIZES;

// ===========================================
// AVATAR DATA TYPES
// ===========================================

/**
 * Thumbnail URLs for different sizes
 */
export interface AvatarThumbnails {
  small: string;   // 32x32
  medium: string;  // 64x64
  large: string;   // 128x128
}

/**
 * School avatar settings (admin configurable)
 */
export interface AvatarSettings {
  allowedRoles: UserRole[];
  maxFileSizeMB: number;
  allowedFormats: string[];
  maxDimensions: {
    width: number;
    height: number;
  };
}

/**
 * Default avatar settings
 * Includes WebP for modern browsers with better compression
 */
export const DEFAULT_AVATAR_SETTINGS: AvatarSettings = {
  allowedRoles: ['ADMIN', 'TEACHER'],
  maxFileSizeMB: 5,
  allowedFormats: ['image/jpeg', 'image/png', 'image/webp'],
  maxDimensions: { width: 2048, height: 2048 },
};

/**
 * Complete avatar information for a user
 * Matches backend UserAvatarInfo shape from avatar.service.ts
 */
export interface UserAvatarInfo {
  userId: string;
  avatarUrl: string | null;
  thumbnails: AvatarThumbnails | null;
  /** User's initials for fallback display (e.g. "AB") */
  initials: string;
  /** Role-based hex color for initials avatar background */
  roleColor: string;
}

/**
 * Result from avatar upload
 */
export interface AvatarUploadResult {
  userId: string;
  avatarUrl: string;
  thumbnails: AvatarThumbnails;
}

// ===========================================
// API REQUEST/RESPONSE TYPES
// ===========================================

/**
 * Response from GET /api/avatar or GET /api/avatar/user/:userId
 */
export interface GetAvatarResponse {
  success: boolean;
  data: UserAvatarInfo;
}

/**
 * Response from POST /api/avatar
 */
export interface UploadAvatarResponse {
  success: boolean;
  message: string;
  data: UserAvatarInfo;
}

/**
 * Response from DELETE /api/avatar
 */
export interface DeleteAvatarResponse {
  success: boolean;
  message: string;
  data: UserAvatarInfo;
}

/**
 * Response from GET /api/avatar/settings
 */
export interface GetAvatarSettingsResponse {
  success: boolean;
  data: AvatarSettings;
}

/**
 * Response from GET /api/avatar/can-upload
 */
export interface CanUploadResponse {
  success: boolean;
  data: {
    canUpload: boolean;
  };
}

// ===========================================
// COMPONENT PROPS TYPES
// ===========================================

/**
 * Props for AvatarDisplay component
 */
export interface AvatarDisplayProps {
  /** User avatar info (from API) */
  avatarInfo?: UserAvatarInfo | null;
  /** Fallback: user's first name */
  firstName?: string;
  /** Fallback: user's last name */
  lastName?: string;
  /** User role for initials color */
  role?: UserRole;
  /** Size of avatar */
  size?: AvatarSize | number;
  /** Additional CSS class */
  className?: string;
  /** Alt text for avatar image */
  alt?: string;
  /** Click handler */
  onClick?: () => void;
}

/**
 * Props for AvatarUpload component
 */
export interface AvatarUploadProps {
  /** Current avatar info */
  currentAvatar?: UserAvatarInfo | null;
  /** User ID to upload for (self if not provided) */
  userId?: string;
  /** Callback when upload succeeds */
  onUploadSuccess?: (avatarInfo: UserAvatarInfo) => void;
  /** Callback when avatar is removed */
  onRemoveSuccess?: (avatarInfo: UserAvatarInfo) => void;
  /** Callback on error */
  onError?: (error: string) => void;
  /** Whether upload is disabled */
  disabled?: boolean;
  /** Avatar settings (for file validation) */
  settings?: AvatarSettings;
}

/**
 * Props for InitialsAvatar component
 */
export interface InitialsAvatarProps {
  /** User's first name */
  firstName: string;
  /** User's last name */
  lastName: string;
  /** User's role (determines color) */
  role: UserRole;
  /** Size in pixels */
  size?: number;
  /** Additional CSS class */
  className?: string;
}

// ===========================================
// HELPER FUNCTIONS
// ===========================================

/**
 * Generate initials from first and last name
 */
export function generateInitials(firstName: string, lastName: string): string {
  const first = firstName?.trim().charAt(0).toUpperCase() || '';
  const last = lastName?.trim().charAt(0).toUpperCase() || '';
  return first + last || '?';
}

/**
 * Get avatar colors for a role
 */
export function getRoleColors(role: UserRole): { background: string; text: string } {
  return ROLE_AVATAR_COLORS[role] || ROLE_AVATAR_COLORS.PARENT;
}

/**
 * Get the appropriate thumbnail URL based on size
 */
export function getThumbnailUrl(
  avatarInfo: UserAvatarInfo | null | undefined,
  size: AvatarSize | number
): string | null {
  if (!avatarInfo?.thumbnails) return avatarInfo?.avatarUrl || null;

  const pixelSize = typeof size === 'number' ? size : AVATAR_SIZES[size];

  if (pixelSize <= 32) return avatarInfo.thumbnails.small;
  if (pixelSize <= 64) return avatarInfo.thumbnails.medium;
  if (pixelSize <= 128) return avatarInfo.thumbnails.large;
  return avatarInfo.avatarUrl;
}

/**
 * Validate file before upload (synchronous checks only)
 */
export function validateAvatarFile(
  file: File,
  settings: AvatarSettings = DEFAULT_AVATAR_SETTINGS
): { valid: boolean; error?: string } {
  // Check file type
  if (!settings.allowedFormats.includes(file.type)) {
    return {
      valid: false,
      error: `Invalid file type. Allowed: ${settings.allowedFormats.join(', ')}`,
    };
  }

  // Check file size
  const maxBytes = settings.maxFileSizeMB * 1024 * 1024;
  if (file.size > maxBytes) {
    return {
      valid: false,
      error: `File too large. Maximum size: ${settings.maxFileSizeMB}MB`,
    };
  }

  // Check minimum size (1KB)
  if (file.size < 1024) {
    return {
      valid: false,
      error: 'File is too small. Please upload a valid image.',
    };
  }

  return { valid: true };
}

/**
 * Get image dimensions from a file (async)
 */
export function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };

    img.src = url;
  });
}

/**
 * Validate image dimensions (async)
 */
export async function validateImageDimensions(
  file: File,
  settings: AvatarSettings = DEFAULT_AVATAR_SETTINGS
): Promise<{ valid: boolean; error?: string; dimensions?: { width: number; height: number } }> {
  try {
    const dimensions = await getImageDimensions(file);

    // Check maximum dimensions
    if (
      dimensions.width > settings.maxDimensions.width ||
      dimensions.height > settings.maxDimensions.height
    ) {
      return {
        valid: false,
        error: `Image too large. Maximum dimensions: ${settings.maxDimensions.width}x${settings.maxDimensions.height}px`,
        dimensions,
      };
    }

    // Check minimum dimensions (at least 32x32 for thumbnails)
    const MIN_DIMENSION = 32;
    if (dimensions.width < MIN_DIMENSION || dimensions.height < MIN_DIMENSION) {
      return {
        valid: false,
        error: `Image too small. Minimum dimensions: ${MIN_DIMENSION}x${MIN_DIMENSION}px`,
        dimensions,
      };
    }

    return { valid: true, dimensions };
  } catch {
    return {
      valid: false,
      error: 'Could not read image dimensions. Please try a different file.',
    };
  }
}

/**
 * Full async validation including dimensions
 */
export async function validateAvatarFileAsync(
  file: File,
  settings: AvatarSettings = DEFAULT_AVATAR_SETTINGS
): Promise<{ valid: boolean; error?: string; dimensions?: { width: number; height: number } }> {
  // First run sync validations
  const syncResult = validateAvatarFile(file, settings);
  if (!syncResult.valid) {
    return syncResult;
  }

  // Then check dimensions
  return validateImageDimensions(file, settings);
}
